import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, Grid, LL_H_C, ModuleFE_Toaster, SimpleListAdapter, TS_DropDown, TS_PropRenderer, TS_Tabs } from '@nu-art/thunderstorm-frontend/index';
import { exists } from '@nu-art/ts-common';
import { QScrollWrapper } from '@app/hcs-frontend';
import './Editor_Organization_EHRInstallations.scss';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText } from '@app/styles/frontend/ui-manager/ui-components';
import { TS_EditableItemComponentProto } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemComponent/TS_EditableItemComponent';
import { EHRInstallation_TypeLabelMap, EHRInstallationTypes } from '@app/org-shared';
import { ViewKey_Edit, ViewKey_View } from '../../../consts.js';
import { Editor_Organization_InstallationOrders } from '../orders/Editor_Organization_InstallationOrders.js';
import { Editor_Organization_InstallationFlowSheets } from '../flow-sheets/Editor_Organization_InstallationFlowSheets.js';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/TS_InputV2';
export class Editor_Organization_EHRInstallations extends TS_EditableItemComponentProto {
    TypeDropDown = TS_DropDown.prepareEditable({
        adapter: SimpleListAdapter([...EHRInstallationTypes], item => _jsx(_Fragment, { children: EHRInstallation_TypeLabelMap[item.item] }))
    });
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.editMode = nextProps.editMode;
        state.organizationId = nextProps.organizationId;
        // should show save button for a new EHR installation on edit mode
        state.showSave = state.editable && !state.editable?.item._id && state.editMode;
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? ViewKey_Edit : ViewKey_View;
    updateEHRInstallation = async (data) => {
        try {
            await this.state.editable?.updateObj(data);
        }
        catch (err) {
            this.logError(err);
        }
        finally {
            this.forceUpdate();
        }
    };
    getTabs = () => {
        const ehrInstallation = this.state.editable?.item;
        const tabs = [];
        tabs.push({
            uid: 'ehr-installations-info',
            title: 'Installation Information',
            content: this.renderForm,
        });
        if (exists(ehrInstallation?._id)) {
            tabs.push({
                uid: 'ehr-installations-orders',
                title: 'Installation Orders',
                content: () => _jsx(Editor_Organization_InstallationOrders, { organizationId: this.state.organizationId, ehrInstallationId: ehrInstallation._id, editMode: this.state.editMode })
            }, {
                uid: 'quai-notes',
                title: 'Quai Notes',
                content: () => _jsx(Editor_Organization_InstallationFlowSheets, { ehrInstallationId: ehrInstallation._id, editMode: this.state.editMode }),
            });
        }
        return tabs;
    };
    // ######################## Render ########################
    render() {
        return _jsx(LL_H_C, { id: 'editor-organization-ehr-installations', children: this.renderTabs() });
    }
    // ######################## Render - All Tabs ########################
    renderTabs = () => {
        if (!this.state.editable)
            return;
        return _jsx(TS_Tabs, { tabs: this.getTabs() });
    };
    // ######################## Render - EHR Installations Editor ########################
    renderForm = () => {
        if (!this.state.editable)
            return _jsx(_Fragment, {});
        const className = _className(this.state.editMode && 'edit-mode');
        return (_jsx("div", { className: "form-wrapper", children: _jsx(QScrollWrapper, { children: _jsxs(Grid, { className: className, children: [this.renderLabel(), this.renderType(), this.renderCustomerId(), this.renderSaveButton()] }) }) }));
    };
    renderSaveButton = () => {
        const editable = this.state.editable;
        const showSave = this.state.showSave;
        if (!showSave)
            return;
        return _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'primary', onClick: async () => {
                        try {
                            await editable?.save();
                            this.setState({ showSave: false });
                        }
                        catch (err) {
                            ModuleFE_Toaster.toastError(`Could not create new installation. Please validate the installation and try again.`);
                            this.logError(err);
                            this.forceUpdate();
                        }
                    }, children: "Save" }), _jsx("div", {})] });
    };
    renderLabel = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Flat, { label: 'Label', error: editable?.hasError('label'), children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: ViewKey_View, renderer: () => _jsx("div", { className: 'grid-data', children: editable?.item.label }) },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label', value: editable?.item.label, onChange: value => this.updateEHRInstallation({ label: value }) })
                    }
                ] }) });
    };
    renderType = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Flat, { label: 'Type', error: editable?.hasError('type'), children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("div", { className: 'grid-data', children: EHRInstallation_TypeLabelMap[editable?.item.type] ?? '-' })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(this.TypeDropDown, { editable: editable, prop: 'type', onSelected: type => this.updateEHRInstallation({ type }) })
                    }
                ] }) });
    };
    renderCustomerId = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Flat, { label: 'Customer ID', error: editable?.hasError('customerId'), children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: ViewKey_View, renderer: () => _jsx("div", { className: 'grid-data', children: editable?.item.customerId ?? '-' }) },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_InputV2, { type: 'number', value: editable?.item.customerId?.toString() ?? '', onChange: value => this.updateEHRInstallation({ customerId: value ? Number(value) : undefined }) })
                    }
                ] }) });
    };
}
//# sourceMappingURL=Editor_Organization_EHRInstallations.js.map